import { useEffect, useRef, useState } from 'preact/hooks';
import {
  type CreateProductInput,
  DashboardApi,
  type Edition,
  HttpError,
  type ProductSummary,
  type Standard,
  type UpdateProductInput,
} from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

const EDITIONS: Edition[] = ['INT', 'WCAG', '508', 'EU'];
const STANDARDS: Standard[] = [
  'WCAG 2.2 Level A',
  'WCAG 2.2 Level AA',
  'WCAG 2.2 Level AAA',
  'WCAG 2.1 Level A',
  'WCAG 2.1 Level AA',
  'WCAG 2.1 Level AAA',
  'EN 301 549',
  'Section 508',
];

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

export function Products({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const [products, setProducts] = useState<ProductSummary[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ProductSummary | null>(null);
  const [creating, setCreating] = useState(false);
  const [portalFor, setPortalFor] = useState<ProductSummary | null>(null);

  const refresh = async () => {
    try {
      const r = await api.listProducts(org.id, showArchived);
      setProducts(r.products);
      setError(null);
    } catch (e) {
      setError(e instanceof HttpError ? e.message : String(e));
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: refresh is stable per render
  useEffect(() => {
    void refresh();
  }, [org.id, showArchived]);

  const onCreate = async (input: CreateProductInput): Promise<void> => {
    await api.createProduct(org.id, input);
    setCreating(false);
    await refresh();
  };

  const onUpdate = async (slug: string, input: UpdateProductInput): Promise<void> => {
    await api.updateProduct(org.id, slug, input);
    setEditing(null);
    await refresh();
  };

  const onArchive = async (slug: string): Promise<void> => {
    if (!confirm(`Archive product "${slug}"? Existing scans + approvals stay accessible.`)) return;
    await api.archiveProduct(org.id, slug);
    await refresh();
  };

  const onRestore = async (slug: string): Promise<void> => {
    await api.restoreProduct(org.id, slug);
    await refresh();
  };

  return (
    <>
      <div class="page-header">
        <h1>Products</h1>
        <p>Each product is a separate scan target with its own VPAT history + defaults.</p>
      </div>

      {error && <Banner variant="warn">{error}</Banner>}

      <div class="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Products in {org.name}</h2>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <label class="small muted">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived((e.target as HTMLInputElement).checked)}
              />{' '}
              Show archived
            </label>
            <button type="button" onClick={() => setCreating(true)}>
              + New product
            </button>
          </div>
        </div>

        {products === null && (
          <p class="small muted">
            <output>Loading…</output>
          </p>
        )}
        {products !== null && products.length === 0 && (
          <div class="empty-state" style={{ padding: '24px 8px' }}>
            <p>No products yet. Create one to start scanning.</p>
          </div>
        )}
        {products !== null && products.length > 0 && (
          <table class="data-table" style={{ width: '100%', marginTop: 16 }}>
            <thead>
              <tr>
                <th>Slug</th>
                <th>Name</th>
                <th>Edition</th>
                <th>Standards</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} style={{ opacity: p.archivedAt ? 0.55 : 1 }}>
                  <td>
                    <code>{p.slug}</code>
                  </td>
                  <td>
                    {p.name}
                    {p.archivedAt && (
                      <span class="pill pill-muted" style={{ marginLeft: 8 }}>
                        archived
                      </span>
                    )}
                  </td>
                  <td>{p.defaultEdition}</td>
                  <td class="small">{p.defaultStandards.join(' · ')}</td>
                  <td class="small muted">{new Date(p.updatedAt).toLocaleDateString()}</td>
                  <td style={{ textAlign: 'right' }}>
                    {p.archivedAt ? (
                      <button type="button" class="link-button" onClick={() => onRestore(p.slug)}>
                        Restore
                      </button>
                    ) : (
                      <>
                        <button type="button" class="link-button" onClick={() => setEditing(p)}>
                          Edit
                        </button>
                        {' · '}
                        <button type="button" class="link-button" onClick={() => setPortalFor(p)}>
                          Portal
                        </button>
                        {' · '}
                        <button type="button" class="link-button" onClick={() => onArchive(p.slug)}>
                          Archive
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {portalFor && (
        <PortalManager org={org} product={portalFor} onClose={() => setPortalFor(null)} />
      )}

      {creating && (
        <ProductForm
          mode="create"
          initial={{
            slug: '',
            name: '',
            defaultEdition: 'INT',
            defaultStandards: ['WCAG 2.2 Level AA'],
          }}
          onCancel={() => setCreating(false)}
          onSubmit={(v) => onCreate(v as CreateProductInput)}
        />
      )}
      {editing && (
        <ProductForm
          mode="edit"
          initial={{
            slug: editing.slug,
            name: editing.name,
            description: editing.description ?? '',
            defaultEdition: editing.defaultEdition,
            defaultStandards: editing.defaultStandards as Standard[],
            defaultVendorName: editing.defaultVendorName ?? '',
            defaultContactInfo: editing.defaultContactInfo ?? '',
            defaultProductDescription: editing.defaultProductDescription ?? '',
            homepageUrl: editing.homepageUrl ?? '',
          }}
          onCancel={() => setEditing(null)}
          onSubmit={(v) => onUpdate(editing.slug, v)}
        />
      )}
    </>
  );
}

interface ProductFormState {
  slug: string;
  name: string;
  description?: string;
  defaultEdition: Edition;
  defaultStandards: Standard[];
  defaultVendorName?: string;
  defaultContactInfo?: string;
  defaultProductDescription?: string;
  homepageUrl?: string;
}

function ProductForm({
  mode,
  initial,
  onCancel,
  onSubmit,
}: {
  mode: 'create' | 'edit';
  initial: ProductFormState;
  onCancel: () => void;
  onSubmit: (v: ProductFormState) => Promise<void>;
}): preact.ComponentChildren {
  const [form, setForm] = useState<ProductFormState>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onSubmit({
        ...form,
        slug: mode === 'create' ? slugify(form.slug || form.name) : form.slug,
      });
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleStandard = (s: Standard) => {
    setForm((f) => ({
      ...f,
      defaultStandards: f.defaultStandards.includes(s)
        ? f.defaultStandards.filter((x) => x !== s)
        : [...f.defaultStandards, s],
    }));
  };

  return (
    <div class="card" style={{ marginTop: 16 }}>
      <h2>{mode === 'create' ? 'New product' : `Edit ${form.name}`}</h2>
      {err && <Banner variant="warn">{err}</Banner>}
      <div class="field">
        <label htmlFor="prod-name">Name</label>
        <input
          id="prod-name"
          type="text"
          value={form.name}
          onInput={(e) => setForm({ ...form, name: (e.target as HTMLInputElement).value })}
        />
      </div>
      {mode === 'create' && (
        <div class="field">
          <label htmlFor="prod-slug">
            Slug (URL identifier — lowercase letters, digits, hyphens)
          </label>
          <input
            id="prod-slug"
            type="text"
            value={form.slug}
            placeholder={slugify(form.name) || 'marketing-site'}
            onInput={(e) => setForm({ ...form, slug: (e.target as HTMLInputElement).value })}
          />
        </div>
      )}
      <div class="field">
        <label htmlFor="prod-desc">Description (optional)</label>
        <input
          id="prod-desc"
          type="text"
          value={form.description ?? ''}
          onInput={(e) => setForm({ ...form, description: (e.target as HTMLInputElement).value })}
        />
      </div>
      <div class="field">
        <label htmlFor="prod-homepage">Homepage URL (optional)</label>
        <input
          id="prod-homepage"
          type="url"
          value={form.homepageUrl ?? ''}
          placeholder="https://acme.test/"
          onInput={(e) => setForm({ ...form, homepageUrl: (e.target as HTMLInputElement).value })}
        />
      </div>

      <fieldset class="field">
        <legend>Default VPAT edition</legend>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {EDITIONS.map((e) => (
            <label key={e} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="radio"
                name="prod-edition"
                value={e}
                checked={form.defaultEdition === e}
                onChange={() => setForm({ ...form, defaultEdition: e })}
              />
              {e}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset class="field">
        <legend>Default standards</legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
          {STANDARDS.map((s) => (
            <label key={s} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={form.defaultStandards.includes(s)}
                onChange={() => toggleStandard(s)}
              />
              {s}
            </label>
          ))}
        </div>
      </fieldset>

      <details class="field">
        <summary>VPAT cover-page defaults (optional — used to skip retyping)</summary>
        <div class="field">
          <label htmlFor="prod-vendor">Vendor name</label>
          <input
            id="prod-vendor"
            type="text"
            value={form.defaultVendorName ?? ''}
            onInput={(e) =>
              setForm({ ...form, defaultVendorName: (e.target as HTMLInputElement).value })
            }
          />
        </div>
        <div class="field">
          <label htmlFor="prod-contact">Contact info</label>
          <input
            id="prod-contact"
            type="text"
            value={form.defaultContactInfo ?? ''}
            onInput={(e) =>
              setForm({ ...form, defaultContactInfo: (e.target as HTMLInputElement).value })
            }
          />
        </div>
        <div class="field">
          <label htmlFor="prod-prod-desc">Product description</label>
          <input
            id="prod-prod-desc"
            type="text"
            value={form.defaultProductDescription ?? ''}
            onInput={(e) =>
              setForm({
                ...form,
                defaultProductDescription: (e.target as HTMLInputElement).value,
              })
            }
          />
        </div>
      </details>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Saving…' : mode === 'create' ? 'Create product' : 'Save changes'}
        </button>
        <button type="button" class="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

interface PortalLink {
  id: string;
  label: string | null;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
  viewCount: number;
  lastViewedAt: string | null;
}

/** Manage read-only client portal links for one product. */
function PortalManager({
  org,
  product,
  onClose,
}: {
  org: AuthOrg;
  product: ProductSummary;
  onClose: () => void;
}): preact.ComponentChildren {
  const [links, setLinks] = useState<PortalLink[] | null>(null);
  const [label, setLabel] = useState('');
  const [created, setCreated] = useState<{ url: string } | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const createdUrlRef = useRef<HTMLElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Dialog focus management: move focus into the panel on open, return it to
  // the triggering "Portal" button on close.
  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panelRef.current?.focus();
    return () => {
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);
  const [gh, setGh] = useState<{
    connected: boolean;
    repo?: string;
    canOpenPrs?: boolean;
  } | null>(null);
  const [ghOwner, setGhOwner] = useState('');
  const [ghRepo, setGhRepo] = useState('');
  const [ghToken, setGhToken] = useState('');
  const [ghBusy, setGhBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.listPortalLinks(org.id, product.slug);
      setLinks(r.portalLinks);
      setGh(await api.getGitHubIntegration(org.id, product.slug));
    } catch (e) {
      setErr(String(e));
      setLinks([]);
    }
  };

  const connectGh = async () => {
    setGhBusy(true);
    setErr(null);
    try {
      await api.connectGitHub(org.id, product.slug, {
        repoOwner: ghOwner.trim(),
        repoName: ghRepo.trim(),
        token: ghToken.trim(),
      });
      setGhToken('');
      setGh(await api.getGitHubIntegration(org.id, product.slug));
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : String(e));
    } finally {
      setGhBusy(false);
    }
  };

  const disconnectGh = async () => {
    if (!confirm('Disconnect GitHub? New scans stop filing issues (existing issues stay).')) return;
    await api.disconnectGitHub(org.id, product.slug);
    setGh(await api.getGitHubIntegration(org.id, product.slug));
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: load stable
  useEffect(() => {
    void load();
  }, [product.slug]);

  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.createPortalLink(org.id, product.slug, {
        ...(label.trim() ? { label: label.trim() } : {}),
      });
      setCreated({ url: r.url });
      setCopyState('idle');
      setLabel('');
      await load();
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    if (!confirm('Revoke this portal link? The client URL will stop working.')) return;
    await api.revokePortalLink(org.id, product.slug, id);
    await load();
  };

  // The token is shown once, so a silent copy failure would lose it. On
  // failure we select the URL text so a manual copy still works, and say so.
  const copyCreatedUrl = async () => {
    if (!created) return;
    try {
      if (!navigator.clipboard) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(created.url);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
      const node = createdUrlRef.current;
      const selection = window.getSelection();
      if (node && selection) {
        const range = document.createRange();
        range.selectNodeContents(node);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  return (
    <div
      class="card"
      style={{ marginTop: 16 }}
      ref={panelRef}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Client portal — {product.name}</h2>
        <button type="button" class="secondary" onClick={onClose}>
          Close
        </button>
      </div>
      <p class="muted small">
        A read-only status page for this product — current conformance, scan-history trend, and
        published VPATs. No account needed; send the URL to the client.
      </p>

      {err && <Banner variant="warn">{err}</Banner>}

      {created && (
        <div class="card" style={{ background: 'rgba(96,165,250,0.08)' }}>
          <p class="small">
            <strong>Portal link created.</strong> Copy it now — the token is shown once.
          </p>
          <code
            ref={createdUrlRef}
            class="small"
            style={{ wordBreak: 'break-all', display: 'block', margin: '8px 0' }}
          >
            {created.url}
          </code>
          <button type="button" onClick={() => void copyCreatedUrl()}>
            {copyState === 'copied' ? 'Copied ✓' : 'Copy link'}
          </button>
          <p class="small" style={{ margin: '8px 0 0' }}>
            <output>
              {copyState === 'copied' && 'Copied to clipboard.'}
              {copyState === 'failed' &&
                "Couldn't copy automatically — the link above is selected; press Ctrl+C (Cmd+C on Mac) to copy it."}
            </output>
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', margin: '12px 0' }}>
        <label class="field" style={{ flex: 1, margin: 0 }}>
          <span class="small">Label (optional)</span>
          <input
            type="text"
            placeholder="e.g. Acme procurement"
            value={label}
            onInput={(e) => setLabel((e.target as HTMLInputElement).value)}
          />
        </label>
        <button type="button" onClick={create} disabled={busy}>
          {busy ? 'Creating…' : 'Create portal link'}
        </button>
      </div>

      {links === null ? (
        <p class="small muted">
          <output>Loading…</output>
        </p>
      ) : links.length === 0 ? (
        <p class="small muted">No portal links yet.</p>
      ) : (
        <table class="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Label</th>
              <th>Created</th>
              <th class="num">Views</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {links.map((l) => (
              <tr key={l.id} style={{ opacity: l.revokedAt ? 0.5 : 1 }}>
                <td>{l.label ?? <span class="muted">—</span>}</td>
                <td class="small muted">{new Date(l.createdAt).toLocaleDateString()}</td>
                <td class="num">{l.viewCount}</td>
                <td style={{ textAlign: 'right' }}>
                  {l.revokedAt ? (
                    <span class="pill pill-muted">revoked</span>
                  ) : (
                    <button type="button" class="link-button" onClick={() => void revoke(l.id)}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: 20, borderTop: '1px solid var(--border, #2a3140)', paddingTop: 16 }}>
        <h3 style={{ margin: '0 0 4px' }}>GitHub Issues</h3>
        <p class="muted small">
          File a GitHub issue for each failing WCAG criterion; close it automatically when a later
          scan finds it fixed. Needs a fine-grained token with <code>issues:write</code> on the
          repo.
        </p>
        {gh?.connected ? (
          <div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span class="pill pill-supports">Connected</span>
              <code class="small">{gh.repo}</code>
              <button type="button" class="link-button" onClick={() => void disconnectGh()}>
                Disconnect
              </button>
            </div>
            {gh.canOpenPrs === false && (
              <p
                class="small"
                style={{
                  margin: '8px 0 0',
                  padding: '8px 10px',
                  borderRadius: 6,
                  background: 'var(--warn-bg, #3a2f1a)',
                  color: 'var(--warn-fg, #e6c07b)',
                }}
              >
                This token can file issues but <strong>can't open pull requests</strong>.
                Auto-PR fixes (source-code changes) need a token with <code>Contents</code> and{' '}
                <code>Pull requests</code> write access — reconnect with a token that grants them.
              </p>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label class="field" style={{ margin: 0 }}>
              <span class="small">Owner</span>
              <input
                type="text"
                placeholder="acme"
                value={ghOwner}
                onInput={(e) => setGhOwner((e.target as HTMLInputElement).value)}
              />
            </label>
            <label class="field" style={{ margin: 0 }}>
              <span class="small">Repo</span>
              <input
                type="text"
                placeholder="site"
                value={ghRepo}
                onInput={(e) => setGhRepo((e.target as HTMLInputElement).value)}
              />
            </label>
            <label class="field" style={{ margin: 0, flex: 1, minWidth: 200 }}>
              <span class="small">Token (github_pat_…)</span>
              <input
                type="password"
                autoComplete="off"
                value={ghToken}
                onInput={(e) => setGhToken((e.target as HTMLInputElement).value)}
              />
            </label>
            <button
              type="button"
              onClick={() => void connectGh()}
              disabled={ghBusy || !ghOwner || !ghRepo || ghToken.length < 20}
            >
              {ghBusy ? 'Connecting…' : 'Connect'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
